"use client";

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Label } from "@/components/atoms/Label";
import { Input } from "@/components/atoms/Input";
import { ChevronDown, X } from "lucide-react";
import { classNameStyles } from "@/components/atoms/Select";
import { cn } from "@/lib/utils";
import type { FieldConfig } from "./types";
import type { BaseFieldProps } from "./types";

interface SearchableMultiSelectFieldProps extends BaseFieldProps {
  field: FieldConfig;
}

const SearchableMultiSelectField: React.FC<SearchableMultiSelectFieldProps> = ({
  fieldName,
  label,
  placeholder,
  required,
  disabled = false,
  hasError,
  validationMessage,
  formData,
  handleChange,
  field,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedIds: string[] = Array.isArray(formData[fieldName])
    ? formData[fieldName]
    : [];

  const options = field.options || [];

  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    options.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, search]);

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuCoords({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 200),
    });
  };

  useLayoutEffect(() => {
    if (!open || disabled) return;
    updateMenuPosition();
  }, [open, disabled]);

  useEffect(() => {
    if (!open || disabled) return;
    const onResizeOrScroll = () => updateMenuPosition();
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, true);
    return () => {
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const addValue = (value: string) => {
    if (selectedIds.includes(value)) return;
    handleChange(fieldName, [...selectedIds, value]);
    setSearch("");
  };

  const removeValue = (value: string) => {
    handleChange(
      fieldName,
      selectedIds.filter((id) => id !== value)
    );
  };

  const menuPortal =
    open &&
    !disabled &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        className={cn(
          "fixed z-[10050] flex max-h-56 flex-col overflow-hidden rounded-md border border-gray-300 bg-white text-black shadow-xl"
        )}
        style={{
          top: menuCoords.top,
          left: menuCoords.left,
          width: menuCoords.width,
        }}
      >
        <div className="border-b border-gray-200 bg-white p-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            classType="admin"
            className="w-full text-sm"
          />
        </div>
        <div className="max-h-44 min-h-0 flex-1 overflow-y-auto bg-white p-1">
          {filteredOptions.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">
              {options.length === 0
                ? "No categories available"
                : "No matches"}
            </p>
          ) : (
            filteredOptions.map((opt) => {
              const taken = selectedIds.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={taken ? "true" : "false"}
                  disabled={taken}
                  className={cn(
                    classNameStyles.itemAdmin,
                    "w-full border-0 bg-white text-left hover:bg-gray-100",
                    taken && "cursor-not-allowed opacity-40"
                  )}
                  onClick={() => !taken && addValue(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div
        className={cn(
          "relative mb-4 flex-1 min-w-80 max-w-md",
          open && !disabled && "z-[1]"
        )}
      >
        {label && (
          <Label
            htmlFor={fieldName}
            className="mb-2 block text-body-3-semibold font-semibold text-black"
          >
            {label}
            {required && <span className="text-secondary-main ml-1">*</span>}
          </Label>
        )}

        {selectedIds.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {selectedIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
              >
                {labelByValue.get(id) || id}
                <button
                  type="button"
                  className="rounded-full p-0.5 text-gray-600 hover:bg-gray-200"
                  onClick={() => removeValue(id)}
                  disabled={disabled}
                  aria-label={`Remove ${labelByValue.get(id) || id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <button
          ref={triggerRef}
          type="button"
          className={cn(
            classNameStyles.triggerAdmin,
            "w-full cursor-pointer text-left",
            hasError &&
              "border-secondary-main focus:border-secondary-main focus:ring-secondary-main",
            disabled && "pointer-events-none opacity-50"
          )}
          onClick={() => !disabled && setOpen((v) => !v)}
          aria-expanded={open ? "true" : "false"}
          aria-haspopup="listbox"
          disabled={disabled}
        >
          <span className="truncate text-sm text-gray-500">
            {placeholder || "Select..."}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>

        {hasError && (
          <p className="text-body-4-medium mt-1 text-secondary-main">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
      {menuPortal}
    </>
  );
};

export default SearchableMultiSelectField;
